import { ProductSettings } from './product-setting.types'

export enum ProductType {
  TICKET = 'ticket',
  GOOD = 'good',
  CHARTER = 'charter',
  MISC = 'miscellaneous',
  FOOD = 'food',
  BEVERAGE = 'beverage',
  ALCOHOL = 'alcohol'
}

export enum ProductOptionType {
  SINGLE_SELECT = 'single_select',
  COUNTER = 'counter'
}

export enum ProductOptionKeyType {
  ROUTE = 'route',
  FARE = 'fare',
  PASSENGER = 'option',
  CUSTOM = 'custom'
}

export type ProductOptionKey = string

/**
 * Important:
 * This is the type that is used across the app.
 * It is the actual information that is used across the app.
 *
 * See type ProductVariantAPI for the actual API response.
 */
export interface ProductVariant {
  id: string
  price: number
  name: string
  settings: ProductSettings
  archived_at?: string | null
  product_type?: ProductType

  /**
   * This options is the actual information that is used across the app
   * Notice that the API returns a different type for the options.
   * This is because the API is legacy and returns a different type for the options.
   * We need to keep the legacy keys for now.
   *
   * See type ProductVariantAPI for the actual API response.
   */
  options: ProductVariantOption[]
}

export interface ProductVariantOption {
  productOptionId: ProductOption['id']
  productOptionValueId: ProductOptionValue['id']
}

export interface GetProductVariant extends Omit<ProductVariant, 'options'> {
  key: ProductOptionKey[]
}

export interface ProductVariantResponse extends GetProductVariant {
  error?: string
  server_error?: string
}

export interface SaveProductVariantResult {
  success: boolean
  data?: ProductVariant
  error?: string
}

export interface UpdateProductVariant extends Omit<ProductVariant, 'options'> {
  options_key: ProductOptionKey[]
}

export interface CreateProductVariant extends Omit<UpdateProductVariant, 'id'> {
  [key: string]: unknown
}

export interface Product {
  id: string
  name: string
  code: string
  type: ProductType
  ledger: string | null
  published_at: string | null
  archived_at: string | null
  options: ProductOption[]
  variants: ProductVariant[]
}

export interface ProductUpdateRequest {
  id?: string
  name?: string
  code?: string
  type?: ProductType
  ledger?: string | null
  published_at?: string | null
  archived_at?: string | null
  variants?: Partial<ProductVariant>[]
  options?: Partial<ProductOption>[]
}

export interface ProductUpdateResponse extends Product {
  error?: string
  server_error?: string
}

/**
 * Product option interface
 */
export interface ProductOption {
  id: ProductOptionKey
  label: string
  key: string
  value: string
  type: ProductOptionType
  active: boolean
  priority: number
  settings: ProductSettings
  values: ProductOptionValue[]
  archived_at: string | null
}

/**
 * Product option value interface
 */
export interface ProductOptionValue {
  id: string
  name: string
  value: string
  description: string
  inserted_at: string
  priority: number
  selected: boolean
  updated_at: string | null
  settings?: Record<string, string | number | boolean>[]

  // @deprecated
  metadata: Record<string, unknown>
}

export interface ICreateProduct {
  name: string
  code: string
  type: ProductType
}

/**
 * Product option status
 */
export enum Status {
  ACTIVE = 'active',
  INACTIVE = 'inactive'
}

export interface ArchiveProductResponse extends Product, ApiError {}

export interface ArchiveProductOptionResponse extends ApiError {
  success?: boolean
}
